import * as zod from 'zod';

import type { ProcessorInfo } from './types';

/**
 * Control-box processors an operator can simulate from the Simulator
 * Controller. Real values, so a simulated box behaves like the hardware it
 * names rather than like an arbitrary core count.
 */
export const ProcessorPresetId = zod.enum([
  'i3-12100',
  'i5-13600K',
  'i7-14700K',
  'i9-14900K',
  'ultra7-265K',
]);

export type ProcessorPresetId = zod.infer<typeof ProcessorPresetId>;

export interface ProcessorPreset {
  id: ProcessorPresetId;
  /** Shown in the picker; the core counts are surfaced alongside it. */
  label: string;
  processor: ProcessorInfo;
}

export const PROCESSOR_PRESETS: ProcessorPreset[] = [
  {
    id: 'i3-12100',
    label: 'Intel i3-12100',
    processor: {
      modelName: 'Intel(R) Core(TM) i3-12100',
      physicalCores: 4,
      pCores: 4,
      eCores: 0,
      threads: 8,
    },
  },
  {
    id: 'i5-13600K',
    label: 'Intel i5-13600K',
    processor: {
      modelName: 'Intel(R) Core(TM) i5-13600K',
      physicalCores: 14,
      pCores: 6,
      eCores: 8,
      threads: 20,
    },
  },
  {
    id: 'i7-14700K',
    label: 'Intel i7-14700K',
    processor: {
      modelName: 'Intel(R) Core(TM) i7-14700K',
      physicalCores: 20,
      pCores: 8,
      eCores: 12,
      threads: 28,
    },
  },
  {
    id: 'i9-14900K',
    label: 'Intel i9-14900K',
    processor: {
      modelName: 'Intel(R) Core(TM) i9-14900K',
      physicalCores: 24,
      pCores: 8,
      eCores: 16,
      threads: 32,
    },
  },
  {
    id: 'ultra7-265K',
    label: 'Intel Core Ultra 7 265K',
    processor: {
      modelName: 'Intel(R) Core(TM) Ultra 7 265K',
      physicalCores: 20,
      pCores: 8,
      eCores: 12,
      // The only preset with no hyperthreading, so threads match cores.
      threads: 20,
    },
  },
];

export function processorPreset(
  id: ProcessorPresetId,
): ProcessorPreset | undefined {
  return PROCESSOR_PRESETS.find((preset) => preset.id === id);
}
